import { useQuery } from "@tanstack/react-query";
import { Mail, ChevronDown, ChevronRight } from "lucide-react";
import { useState } from "react";
import { api } from "@/lib/api-client";
import { EmptyState } from "@/components/common/EmptyState";
import { StatusBadge } from "@/components/common/StatusBadge";
import { useTranslation } from "@/hooks/useTranslation";
import { formatDateTime } from "@/lib/format";
import type { Share, ExternalResponse } from "@/types";

// ─────────────────────────────────────────────
// ExternalResponsesTab
//
// Fetches the assessment's shares (GET /shares?assessmentId=), then lazily
// loads each share's external responses (GET /shares/:id/responses) on
// expand. Read-only.
// Design: §8.5 Sharing UI — External Responses
// ─────────────────────────────────────────────

interface ExternalResponsesTabProps {
  assessmentId: string;
}

interface SharesApiResponse {
  data: Share[];
}

interface ResponsesApiResponse {
  data: ExternalResponse[];
}

function formatResponseValue(value: unknown, t: (key: string) => string): string {
  if (value === null || value === undefined || value === "") return t("sharing.noAnswer");
  if (typeof value === "object") return JSON.stringify(value);
  return String(value);
}

function ShareResponsesRow({ share }: { share: Share }) {
  const { t, language } = useTranslation();
  const [expanded, setExpanded] = useState(false);

  const { data, isLoading } = useQuery<ResponsesApiResponse>({
    queryKey: ["share-responses", share.id],
    queryFn: () => api.get(`/shares/${share.id}/responses`),
    enabled: expanded,
  });

  const responses = data?.data ?? [];
  // The share record itself has no "submitted at" field — derive it from the
  // most recent response timestamp so the table can show when (if ever) this
  // recipient last completed the questionnaire.
  const lastSubmittedAt = responses.reduce<string | null>((latest, r) => {
    if (!r.submittedAt) return latest;
    return !latest || new Date(r.submittedAt) > new Date(latest) ? r.submittedAt : latest;
  }, null);

  return (
    <>
      <tr
        className="border-b border-border-subtle cursor-pointer hover:bg-hover last:border-0"
        onClick={() => setExpanded((v) => !v)}
      >
        <td className="w-8 px-3 py-3.5">
          {expanded ? (
            <ChevronDown className="h-3.5 w-3.5 text-text-muted" />
          ) : (
            <ChevronRight className="h-3.5 w-3.5 text-text-muted" />
          )}
        </td>
        <td className="px-3 py-3.5">
          <div className="flex items-center gap-2">
            <Mail className="h-4 w-4 text-text-muted flex-shrink-0" />
            <span className="text-body text-text-primary">{share.externalEmail}</span>
          </div>
        </td>
        <td className="px-3 py-3.5">
          <StatusBadge type="share" status={share.status} />
        </td>
        <td className="px-3 py-3.5 text-text-secondary">{share.accessCount}</td>
        <td className="px-3 py-3.5 text-text-secondary">
          {!expanded ? (
            <span className="text-text-muted">-</span>
          ) : isLoading ? (
            <span className="text-text-muted">…</span>
          ) : lastSubmittedAt ? (
            formatDateTime(lastSubmittedAt, language)
          ) : (
            <span className="text-text-muted">{t("sharing.notSubmitted")}</span>
          )}
        </td>
      </tr>
      {expanded && (
        <tr className="border-b border-border-subtle bg-hover">
          <td colSpan={5} className="px-6 py-3">
            {isLoading ? (
              <p className="py-2 text-caption text-text-muted">{t("sharing.loadingResponses")}</p>
            ) : responses.length === 0 ? (
              <EmptyState
                icon={Mail}
                title={t("sharing.noResponsesYet")}
                description={t("sharing.noResponsesYetDesc")}
                className="py-4"
              />
            ) : (
              <div className="flex flex-col gap-2">
                <p className="text-caption font-medium uppercase tracking-wider text-text-muted">
                  {t("sharing.responsesWithCount", { count: responses.length })}
                </p>
                {responses.map((r) => (
                  <div key={r.id} className="flex flex-col gap-0.5">
                    <div className="flex items-center gap-2">
                      <span className="font-mono text-[11px] text-text-muted">{r.questionId}</span>
                      {r.postureId && (
                        <span className="font-mono text-[10px] text-text-muted">({r.postureId})</span>
                      )}
                    </div>
                    <p className="ml-2 text-body text-text-primary">
                      {formatResponseValue(r.response, t)}
                    </p>
                  </div>
                ))}
              </div>
            )}
          </td>
        </tr>
      )}
    </>
  );
}

export function ExternalResponsesTab({ assessmentId }: ExternalResponsesTabProps) {
  const { t } = useTranslation();
  const { data, isLoading } = useQuery<SharesApiResponse>({
    queryKey: ["assessment-shares", assessmentId],
    queryFn: () => api.get(`/shares?assessmentId=${assessmentId}`),
    enabled: Boolean(assessmentId),
  });

  const shares = data?.data ?? [];

  if (isLoading) {
    return (
      <div className="py-6 text-center text-body text-text-muted">
        {t("sharing.loadingExternalResponses")}
      </div>
    );
  }

  if (shares.length === 0) {
    return (
      <EmptyState
        icon={Mail}
        title={t("sharing.noExternalResponses")}
        description={t("sharing.noExternalResponsesDesc")}
      />
    );
  }

  return (
    <div className="overflow-hidden rounded-card border border-border-default bg-card">
      <div className="overflow-x-auto">
        <table className="w-full border-collapse text-body">
          <thead>
            <tr className="border-b border-border-default">
              <th className="w-8 px-3 py-3" />
              <th className="px-3 py-3 text-left text-table-header font-medium uppercase tracking-table-header text-text-muted">
                {t("sharing.respondent")}
              </th>
              <th className="px-3 py-3 text-left text-table-header font-medium uppercase tracking-table-header text-text-muted">
                {t("sharing.status")}
              </th>
              <th className="px-3 py-3 text-left text-table-header font-medium uppercase tracking-table-header text-text-muted">
                {t("sharing.accesses")}
              </th>
              <th className="px-3 py-3 text-left text-table-header font-medium uppercase tracking-table-header text-text-muted">
                {t("sharing.submitted")}
              </th>
            </tr>
          </thead>
          <tbody>
            {shares.map((share) => (
              <ShareResponsesRow key={share.id} share={share} />
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
